/**
 * Canonical content-language contract (Admin copy).
 *
 * This contract is deliberately duplicated per runtime rather than extracted
 * into a shared package. The normalization semantics below are normative and
 * must stay identical to the copies in the Autofocus Highlights service and the
 * AI generation service; the contract test table is duplicated verbatim
 * alongside each copy.
 */

export type ContentLanguage = 'en' | 'es' | 'unknown'

/** A language resolved to a concrete target. Never `unknown`. */
export type ResolvedContentLanguage = Exclude<ContentLanguage, 'unknown'>

export type HighlightLanguageSetting = 'same-as-description' | 'en' | 'es'

export const DEFAULT_DESCRIPTION_LANGUAGE: ResolvedContentLanguage = 'en'
export const DEFAULT_HIGHLIGHT_LANGUAGE_SETTING: HighlightLanguageSetting = 'same-as-description'

/** Per-VIN classifier decision, resolved independently for each content type. */
export type ContentLanguageOperation =
  | 'generate'
  | 'translate'
  | 'restore'
  | 'skip-locked'
  | 'skip-already-correct'
  | 'skip-ineligible'
  | 'error'

const EN_ALIASES = new Set(['en', 'eng', 'english'])
const ES_ALIASES = new Set(['es', 'spa', 'spanish', 'español', 'espanol', 'castellano'])

export const normalizeLanguage = (input: unknown): ContentLanguage => {
  if (typeof input !== 'string') return 'unknown'
  const trimmed = input.trim().toLowerCase()
  if (trimmed === '') return 'unknown'
  const base = trimmed.replace(/_/g, '-').split('-')[0]
  if (EN_ALIASES.has(base)) return 'en'
  if (ES_ALIASES.has(base)) return 'es'
  return 'unknown'
}

export const isResolvedContentLanguage = (input: unknown): input is ResolvedContentLanguage =>
  input === 'en' || input === 'es'

export const normalizeHighlightLanguageSetting = (input: unknown): HighlightLanguageSetting => {
  if (typeof input === 'string') {
    if (input.trim().toLowerCase() === 'same-as-description') return 'same-as-description'
    const normalized = normalizeLanguage(input)
    if (normalized !== 'unknown') return normalized
  }
  return DEFAULT_HIGHLIGHT_LANGUAGE_SETTING
}

export interface ContentLanguageSettings {
  descriptionLanguage?: unknown
  highlightLanguage?: unknown
}

export interface ResolvedContentLanguages {
  descriptionLanguage: ResolvedContentLanguage
  highlightLanguage: ResolvedContentLanguage
}

/**
 * Resolve both account settings to concrete target languages, so the Admin can
 * show the language that will actually be applied when the highlight setting is
 * Same as Description.
 */
export const resolveTargetLanguages = (
  settings: ContentLanguageSettings | null | undefined,
): ResolvedContentLanguages => {
  const description = normalizeLanguage(settings?.descriptionLanguage)
  const descriptionLanguage = description === 'unknown' ? DEFAULT_DESCRIPTION_LANGUAGE : description

  const highlightSetting = normalizeHighlightLanguageSetting(settings?.highlightLanguage)
  const highlightLanguage =
    highlightSetting === 'same-as-description' ? descriptionLanguage : highlightSetting

  return { descriptionLanguage, highlightLanguage }
}

/** Display label for a resolved language. */
export const LANGUAGE_LABELS: Record<ResolvedContentLanguage, string> = {
  en: 'English',
  es: 'Spanish',
}

export const getLanguageLabel = (input: unknown): string => {
  const normalized = normalizeLanguage(input)
  return normalized === 'unknown' ? 'Unknown' : LANGUAGE_LABELS[normalized]
}
